/**
 * Photo Capture Service
 * 
 * Handles photo capture functionality for Chef reminders.
 * Provides camera access and photo storage for session reports.
 */

class PhotoCaptureService {
    constructor() {
        this.isSupported = this.checkCameraSupport();
    }

    /**
     * Check if camera is supported
     */
    checkCameraSupport() {
        return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }

    /**
     * Capture a photo using the device camera
     */
    async capturePhoto() {
        if (!this.isSupported) {
            throw new Error('Camera not supported on this device');
        }

        try {
            // Request camera permission
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { 
                    facingMode: 'environment', // Prefer back camera
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                } 
            });

            // Create video element to capture frame
            const video = document.createElement('video');
            video.srcObject = stream;
            video.play();

            // Wait for video to be ready
            await new Promise((resolve) => {
                video.onloadedmetadata = resolve;
            });

            // Create canvas to capture frame
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            
            // Set canvas dimensions to match video
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            
            // Draw video frame to canvas
            context.drawImage(video, 0, 0);
            
            // Stop the camera stream
            stream.getTracks().forEach(track => track.stop());
            
            // Convert canvas to blob
            return new Promise((resolve, reject) => {
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to capture photo'));
                    }
                }, 'image/jpeg', 0.8);
            });

        } catch (error) {
            console.error('Error capturing photo:', error);
            throw error;
        }
    }

    /**
     * Capture photo with user interface
     */
    async capturePhotoWithUI() {
        return new Promise((resolve, reject) => {
            // Create modal for photo capture
            const modal = document.createElement('div');
            modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
            
            const modalContent = document.createElement('div');
            modalContent.className = 'bg-white rounded-lg p-6 max-w-md w-full mx-4';
            
            // Detect mobile device
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            
            modalContent.innerHTML = `
                <div class="text-center">
                    <h3 class="text-lg font-medium mb-4">Capture Photo</h3>
                    <div class="mb-4">
                        <video id="camera-preview" class="w-full h-64 bg-gray-200 rounded ${isMobile ? 'hidden' : ''}" autoplay></video>
                        <div id="preview-placeholder" class="w-full h-64 bg-gray-200 rounded flex items-center justify-center ${isMobile ? '' : 'hidden'}">
                            <span class="text-gray-500">Select photo source below</span>
                        </div>
                    </div>
                    <div class="flex gap-3 justify-center flex-col">
                        <label for="file-input" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer text-center">
                            📱 Use Phone Camera / Upload
                        </label>
                        <input type="file" id="file-input" accept="image/*" capture="environment" class="hidden" />
                        <button id="capture-btn" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${isMobile ? 'hidden' : ''}">
                            📸 Use Computer Camera
                        </button>
                        <button id="cancel-btn" class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
                            Cancel
                        </button>
                    </div>
                </div>
            `;
            
            modal.appendChild(modalContent);
            document.body.appendChild(modal);
            
            const video = modalContent.querySelector('#camera-preview');
            const captureBtn = modalContent.querySelector('#capture-btn');
            const cancelBtn = modalContent.querySelector('#cancel-btn');
            const fileInput = modalContent.querySelector('#file-input');
            
            let stream = null;
            
            // Handle file input (phone camera / upload)
            fileInput.addEventListener('change', async (e) => {
                const file = e.target.files[0];
                if (file) {
                    try {
                        // Stop camera stream if active
                        if (stream) {
                            stream.getTracks().forEach(track => track.stop());
                        }
                        
                        // Convert file to blob
                        const blob = new Blob([await file.arrayBuffer()], { type: file.type });
                        resolve(blob);
                        document.body.removeChild(modal);
                    } catch (error) {
                        reject(error);
                        document.body.removeChild(modal);
                    }
                }
            });
            
            // Setup computer camera (only if not mobile)
            if (!isMobile) {
                navigator.mediaDevices.getUserMedia({ 
                    video: { 
                        facingMode: 'environment',
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                    } 
                }).then((cameraStream) => {
                    stream = cameraStream;
                    video.srcObject = stream;
                    video.classList.remove('hidden');
                    modalContent.querySelector('#preview-placeholder').classList.add('hidden');
                }).catch((error) => {
                    console.warn('Camera access failed, file upload still available:', error);
                });
            }
            
            // Capture photo from computer camera
            captureBtn.addEventListener('click', async () => {
                try {
                    if (!stream) {
                        alert('Camera not available. Please use the file upload option.');
                        return;
                    }
                    
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    context.drawImage(video, 0, 0);
                    
                    // Stop camera
                    stream.getTracks().forEach(track => track.stop());
                    
                    // Convert to blob
                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to capture photo'));
                        }
                        document.body.removeChild(modal);
                    }, 'image/jpeg', 0.8);
                    
                } catch (error) {
                    reject(error);
                    document.body.removeChild(modal);
                }
            });
            
            // Cancel
            cancelBtn.addEventListener('click', () => {
                if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                }
                document.body.removeChild(modal);
                reject(new Error('Photo capture cancelled'));
            });
        });
    }

    /**
     * Compress and resize image before converting to base64
     */
    async compressImage(blob, maxWidth = 1024, quality = 0.8) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const url = URL.createObjectURL(blob);
            
            img.onload = () => {
                URL.revokeObjectURL(url);
                
                // Calculate new dimensions
                let width = img.width;
                let height = img.height;
                
                if (width > maxWidth) {
                    height = Math.round(height * maxWidth / width);
                    width = maxWidth;
                }
                
                // Create canvas and resize
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                
                // Convert to blob with compression
                canvas.toBlob(
                    (compressedBlob) => {
                        if (compressedBlob) {
                            // console.log(`📸 Image compressed: ${blob.size} → ${compressedBlob.size} bytes (${Math.round(compressedBlob.size / blob.size * 100)}%)`);
                            resolve(compressedBlob);
                        } else {
                            reject(new Error('Failed to compress image'));
                        }
                    },
                    'image/jpeg',
                    quality
                );
            };
            
            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load image'));
            };
            
            img.src = url;
        });
    }

    /**
     * Convert blob to base64 for storage
     */
    async blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Create photo data object for session report
     */
    createPhotoData(blob, reminder) {
        return {
            id: `photo_${Date.now()}`,
            timestamp: new Date().toISOString(),
            stepIndex: reminder.stepIndex,
            trigger: reminder.trigger,
            reminderMessage: reminder.message,
            blob: blob,
            size: blob.size,
            type: blob.type
        };
    }
}

// Create singleton instance
const photoCapture = new PhotoCaptureService();

export default photoCapture;
